/**
 * The /help guide (English). Moved out of the page so the server can translate
 * it once per language and cache it (/api/help/translate — Gordon 2026-11-02:
 * "make sure they see the whole site in French"). Icons are lucide names,
 * mapped to components in the page.
 */

export interface HelpStep { title: string; detail: string }
export interface HelpFeature {
  name: string;
  summary: string;
  steps: HelpStep[];
  imageUrl?: string;
  videoId?: string;
  userTypes?: string[];
  badge?: string;
}
export interface HelpSection {
  id: string;
  icon: string;
  title: string;
  color: string;
  bg: string;
  intro: string;
  features: HelpFeature[];
}

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: "getting-started",
    icon: 'Home',
    title: "Getting Started",
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    intro: "Everything you need to sign up, choose your account type, and find your way around Breakin' Bread.",
    features: [
      {
        name: "Creating your account",
        summary: "Sign up with email or Google in seconds.",
        steps: [
          { title: "Go to Sign In", detail: "Tap the Sign In button in the sidebar or visit /signin. You can sign in with Google (one tap) or your email address." },
          { title: "Choose your account type", detail: "On the onboarding screen you'll pick: Consumer (listener + buyer), Business Consumer (consumer + business tools), Creator (upload music + sell), or Venue (post events + manage bookings)." },
          { title: "Set your profile", detail: "Add a display name and avatar. Creator accounts can also select specific roles — producer, DJ, vocalist, etc." },
          { title: "You're in", detail: "You're redirected to the Home Feed. Your account type determines which tools appear in your sidebar." },
        ],
      },
      {
        name: "Navigating the app",
        summary: "Sidebar on desktop, bottom bar on mobile.",
        steps: [
          { title: "Desktop sidebar", detail: "The left sidebar shows all main sections. Drag the grip handle next to any item to reorder the nav to your preference — your order is saved to your account." },
          { title: "Mobile bottom bar", detail: "On phones, the 5 bottom bar buttons cover Home, Search, your main tab, and Profile. Swipe up from the VU strip to expand the media player." },
          { title: "App header", detail: "The top bar always shows the app logo, Favorites bar (your saved tracks), search, language switcher, Help Mode toggle (?), and your avatar." },
        ],
      },
      {
        name: "Make the top-left icon your own",
        summary: "Upload a personal logo that replaces the app icon while you're signed in.",
        steps: [
          { title: "Go to Profile → Settings", detail: "Open your Profile page and scroll to the logo settings in the Settings section." },
          { title: "Upload your logo", detail: "Upload a square image under \"Your Logo\". It replaces the app icon in the top-left corner of the sidebar whenever you're signed in — your personal brand, front and centre." },
          { title: "What visitors see", detail: "Logged-out visitors see a rotating slideshow of content thumbnails from across the app instead, with the Breakin' Bread logo reappearing every 9th slide." },
        ],
      },
      {
        name: "Connect your own domain",
        summary: "Point your own domain name at your artist page.",
        steps: [
          { title: "Open Edit Profile → Artist Site", detail: "On your Profile page, open Edit Profile and switch to the \"Artist Site\" tab." },
          { title: "Enter your domain", detail: "Type the domain you own (e.g. yourname.com) and follow the DNS instructions shown — usually one CNAME record at your domain registrar." },
          { title: "Verify & go live", detail: "Once DNS propagates, your domain serves your Breakin' Bread artist page directly — fans can find you at your own address while everything stays managed in one place." },
        ],
      },
      {
        name: "Dark / Light mode",
        summary: "Switch themes instantly from the sidebar.",
        steps: [
          { title: "Open the theme picker", detail: "Tap the Palette icon in the bottom of the sidebar." },
          { title: "Pick a theme", detail: "Choose from Dark, Light, or System. Your preference is saved immediately — no page reload." },
        ],
      },
      {
        name: "Language switcher",
        summary: "Change the app language from the top bar.",
        steps: [
          { title: "Tap the globe icon", detail: "The 🌐 icon in the top header opens the language selector." },
          { title: "Choose a language", detail: "Select your language from the list. The app switches instantly." },
        ],
      },
      {
        name: "Help Mode",
        summary: "Turn on tooltip bubbles for every element in the app.",
        steps: [
          { title: "Tap the ? button", detail: "The ? icon in the top-right header toggles Help Mode on and off." },
          { title: "See blue dots appear", detail: "Every interactive element gets a pulsing blue dot. Hover or tap any dot to see what it does." },
          { title: "Turn off when done", detail: "Tap ? again to hide all the dots and return to normal." },
        ],
        badge: "Pro tip",
      },
    ],
  },

  {
    id: "home-feed",
    icon: 'LayoutGrid',
    title: "Home Feed",
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    intro: "Your main dashboard — scrollable sections for every type of content, with filters, sorts, and view modes.",
    features: [
      {
        name: "Section filter bar",
        summary: "Jump instantly to any section — Artists, Music, Events, Radio…",
        steps: [
          { title: "The sticky pill bar", detail: "The horizontal bar just below the app header shows pill buttons for each section on the page (Artists, Music, Events, Radio, News, Marketplace…). Tap any pill to scroll directly to that section." },
          { title: "Show / hide the bar", detail: "Tap the sliders (⊞) icon on the right of the filter bar to toggle it. When hidden, sections collapse into a more compact view." },
          { title: "Drag to scroll", detail: "On mobile, press and drag the pill bar sideways to scroll through all available section chips." },
        ],
      },
      {
        name: "Content modes: All / Following / Suggested",
        summary: "Filter the entire feed by your taste.",
        steps: [
          { title: "All", detail: "Shows every published piece of content across the platform — the default view." },
          { title: "Following", detail: "Shows only content from artists and users you follow. Great for keeping up with your favourite creators." },
          { title: "Suggested", detail: "AI-curated picks based on what you've listened to, saved, and interacted with. Discovers new music you'll likely love." },
        ],
      },
      {
        name: "Section view toggles",
        summary: "Switch between list, grid, compact grid, or horizontal scroll per section.",
        steps: [
          { title: "Find the view icons", detail: "Each section has small view-mode icons in its header row (☰ list, ⊞ grid, ▦ compact, ↔ horizontal)." },
          { title: "Pick your layout", detail: "Tap an icon to switch that section. Your choice per section is saved and remembered next time." },
        ],
      },
      {
        name: "Section sort",
        summary: "Sort any section by newest, featured, random, A–Z, popularity, or price.",
        steps: [
          { title: "Tap the sort arrow icon", detail: "Each section header has a sort button (▲▼). Tap it to cycle through: Default → Featured → Newest → A–Z → Popular → Price → Random." },
          { title: "Reverse direction", detail: "Tap the active sort again to flip the order (e.g. A–Z → Z–A)." },
        ],
      },
      {
        name: "Hero slideshow",
        summary: "The top visual area plays featured videos and highlights.",
        steps: [
          { title: "Auto-plays on load", detail: "The hero area at the top of the home feed cycles through featured videos and visual content automatically." },
          { title: "Volume control", detail: "Tap the speaker icon (🔊) on the hero to mute or unmute the video. Your mute preference is remembered across slides." },
        ],
      },
      {
        name: "Favorites bar",
        summary: "Your saved tracks and videos — always in the header for instant playback.",
        steps: [
          { title: "Heart anything", detail: "Tap the ♥ icon on any track, release, or video card. It appears in your Favorites bar immediately." },
          { title: "Play from the bar", detail: "The scrollable thumbnail strip in the header is your favorites. Tap any thumbnail to play it instantly from anywhere in the app." },
          { title: "Remove a favourite", detail: "Tap the ♥ icon again on an item to remove it from your bar." },
        ],
      },
    ],
  },

  {
    id: "music",
    icon: 'Music2',
    title: "Music & Releases",
    color: "text-purple-500",
    bg: "bg-purple-500/10",
    intro: "Stream tracks, explore releases, read lyrics, leave timestamped comments, and download music.",
    features: [
      {
        name: "Playing a track",
        summary: "Tap any track card to start playing.",
        steps: [
          { title: "Tap the card", detail: "Any track card has a ▶ play button. Tapping anywhere on the card queues and plays it in the media player bar at the bottom." },
          { title: "Open the track page", detail: "Tap the track title or artwork to go to the full track detail page — waveform, lyrics, credits, comments, and download button." },
        ],
      },
      {
        name: "Waveform visualizer",
        summary: "See the audio waveform and click anywhere to seek.",
        steps: [
          { title: "View the waveform", detail: "On any track detail page, the waveform canvas shows the full audio shape. The playback cursor moves in real time." },
          { title: "Click to seek", detail: "Click or tap anywhere on the waveform to jump to that position in the track — works like a visual progress bar." },
          { title: "Comment pins", detail: "Orange triangle markers on the waveform show where other listeners left timestamped comments. Hover a pin to preview the comment." },
        ],
      },
      {
        name: "Timeline comments",
        summary: "Leave a comment pinned to the exact moment in a track.",
        steps: [
          { title: "Play to where you want to comment", detail: "Let the track play to the moment that inspired your thought — the comment will be pinned there." },
          { title: "Tap 'Add comment'", detail: "The comment form captures the current playback position automatically as a timestamp." },
          { title: "Write and post", detail: "Type your comment and hit Post. It appears as a pin on the waveform for all listeners." },
        ],
      },
      {
        name: "Lyrics & Karaoke mode",
        summary: "Read lyrics or follow along in sync with the music.",
        steps: [
          { title: "Open lyrics", detail: "Scroll down on a track page to the Lyrics section. Lyrics show when the creator has added them." },
          { title: "Translate lyrics", detail: "Tap 'Translate' and pick a language from the dropdown. AI translates into 20+ languages and caches the result for the session." },
          { title: "Enable Karaoke mode", detail: "Creators and admins can switch on Karaoke mode — each line highlights and scrolls in sync with the playback position, like a teleprompter." },
        ],
        userTypes: ["creator", "admin"],
      },
      {
        name: "Downloading music",
        summary: "Download free, pay-what-you-want, or paid tracks.",
        steps: [
          { title: "Find the Download button", detail: "Tracks with downloads enabled show a ⬇ Download button on their detail page." },
          { title: "Free tracks", detail: "Click Download — the file downloads immediately, no checkout required." },
          { title: "Paid / PWYW tracks", detail: "A dialog opens. For Pay What You Want tracks, enter any amount at or above the minimum. For paid tracks, enter your card details via Stripe. You receive a download link after payment." },
        ],
      },
      {
        name: "Releases (Albums & EPs)",
        summary: "Explore full albums with track listings, credits, and cover art.",
        steps: [
          { title: "Find a release", detail: "Releases appear in the Releases section on the home feed, on artist pages, and in search results." },
          { title: "Release page", detail: "Each release shows cover art, the full track listing (tap any row to play), release year, label, contributors, and credits." },
          { title: "Play the full album", detail: "Tap 'Play All' to queue the entire release in the media player." },
        ],
      },
    ],
  },

  {
    id: "media-player",
    icon: 'Play',
    title: "Media Player",
    color: "text-orange-500",
    bg: "bg-orange-500/10",
    intro: "The persistent audio/video player at the bottom of the screen — controls, queue, and full-screen mode.",
    features: [
      {
        name: "Basic controls",
        summary: "Play, pause, skip, and seek.",
        steps: [
          { title: "Play / Pause", detail: "The ▶/⏸ button in the centre of the player bar. Also available on every card and track page." },
          { title: "Skip tracks", detail: "⏮ goes to previous track, ⏭ goes to next in the queue." },
          { title: "Seek", detail: "Click or tap anywhere on the progress bar (the thin line above the player bar) to jump to that position." },
        ],
      },
      {
        name: "Shuffle & Repeat",
        summary: "Randomise your queue or loop a track.",
        steps: [
          { title: "Shuffle", detail: "Tap the ⇌ shuffle icon to play your queue in random order. Tap again to turn off." },
          { title: "Repeat", detail: "Tap the 🔁 icon to loop the current track. The icon turns highlighted when active." },
        ],
      },
      {
        name: "Volume",
        summary: "Adjust volume from the player.",
        steps: [
          { title: "Speaker icon", detail: "Tap the 🔊 icon in the player bar to reveal a volume slider. Drag to adjust." },
          { title: "Mobile", detail: "On phones use your device's hardware volume buttons — they control app audio directly." },
        ],
      },
      {
        name: "VU Strip",
        summary: "The live audio level strip at the very bottom of the screen.",
        steps: [
          { title: "What it is", detail: "The thin animated bar at the bottom edge of every page shows live audio levels in real time while a track is playing." },
          { title: "Tap to expand", detail: "Tap the VU strip to open the full player sheet — large artwork, waveform, full controls, and lyrics." },
          { title: "Always flush", detail: "The strip sits directly above the mobile navigation bar with zero gap." },
        ],
      },
      {
        name: "Playing videos",
        summary: "The media player handles YouTube videos too.",
        steps: [
          { title: "Heart a video", detail: "Tap ♥ on any video card in the Videos section on the home feed or an artist page." },
          { title: "Play from Favorites bar", detail: "The video thumbnail appears in your Favorites bar. Tap it to open the YouTube embed in the player." },
          { title: "Full screen", detail: "Use the YouTube player controls inside the embed for full screen." },
        ],
      },
    ],
  },

  {
    id: "artists",
    icon: 'Users',
    title: "Artists & Labels",
    color: "text-pink-500",
    bg: "bg-pink-500/10",
    intro: "Explore artist profiles, follow creators, browse labels, and discover curated web info.",
    features: [
      {
        name: "Artist page",
        summary: "Full profile — bio, music, releases, videos, gallery, and social links.",
        steps: [
          { title: "Get there", detail: "Tap any artist name or avatar in the home feed, search results, or a track's artist credit." },
          { title: "Tabs", detail: "The artist page has tabs: Wall, Music, Videos, Releases, Gallery, Marketplace, and Playlists. Tap any tab to switch." },
          { title: "Gallery", detail: "The Gallery tab shows all images from the artist's profile — photos, artwork, and press shots." },
          { title: "Web Info", detail: "Scroll down to the Web Info section — shows the linked Wikipedia article, Discogs profile, and admin-curated external links (Spotify, SoundCloud, etc.)." },
        ],
      },
      {
        name: "Following an artist",
        summary: "Follow to see their content in your Following feed.",
        steps: [
          { title: "Tap Follow", detail: "The Follow button is on every artist page header. Tap it to follow." },
          { title: "Following feed", detail: "Once you follow, their content appears in the 'Following' mode on the home feed filter bar." },
          { title: "Unfollow", detail: "Tap Following → Unfollow on the same button." },
        ],
      },
      {
        name: "Labels",
        summary: "Record labels have their own pages listing their full roster.",
        steps: [
          { title: "Find a label", detail: "Labels appear in the home feed (Labels section), on artist pages (label credit), and in search results." },
          { title: "Label page", detail: "Each label page lists all their signed artists and releases." },
        ],
      },
      {
        name: "QR share",
        summary: "Generate a QR code for any artist, release, or event.",
        steps: [
          { title: "Tap the QR icon", detail: "Every artist page, track page, release, event, and marketplace item has a ▣ QR button." },
          { title: "Full-screen QR", detail: "A dialog opens showing the entity's thumbnail above a large QR code." },
          { title: "Share it", detail: "Use 'Copy Link' to copy the URL, or 'Save QR' to download the QR image to your camera roll." },
        ],
      },
    ],
  },

  {
    id: "chat",
    icon: 'MessageSquare',
    title: "Chat & Messaging",
    color: "text-teal-500",
    bg: "bg-teal-500/10",
    intro: "Direct messages, group conversations, and the BB AI Advisor — all in one window.",
    features: [
      {
        name: "Opening the chat",
        summary: "The button in the bottom-right corner is always visible.",
        steps: [
          { title: "Tap the chat FAB", detail: "The floating action button in the bottom-right corner is always on screen — even on mobile above the player bar." },
          { title: "Logged out?", detail: "If you're not signed in, tapping chat redirects you to the sign-in page." },
          { title: "Resize the window", detail: "Use the expand/compress icons in the chat header to switch between quarter, half, and full-screen modes." },
        ],
      },
      {
        name: "Direct messages",
        summary: "Private 1-on-1 conversations with any user.",
        steps: [
          { title: "Find a contact", detail: "Open the Contacts tab in the chat window. Admins see all platform users. Creators and consumers see their followers and people they follow." },
          { title: "Start a conversation", detail: "Tap a contact to open a direct message thread. Type and hit Send (or press Enter)." },
          { title: "Message from an artist page", detail: "On any artist's profile page, tap the Message button to start a DM directly." },
        ],
      },
      {
        name: "BB AI Advisor",
        summary: "AI expert across 6 music-industry domains.",
        steps: [
          { title: "Open the Advisor tab", detail: "In the chat window, tap the 'Advisor' tab (sparkles icon)." },
          { title: "Choose a domain", detail: "Pick from: Legal, Finance, Marketing, Music Business, Publishing, or Sync. Each domain has a tailored AI specialist." },
          { title: "Ask anything", detail: "Type your question or pick a starter question from the suggested list. The AI answers with specific, actionable advice." },
          { title: "Continue the conversation", detail: "The AI remembers the last few messages in the thread for context. Start a new domain selection to reset." },
        ],
      },
      {
        name: "Notification sounds",
        summary: "Configure message alert sounds.",
        steps: [
          { title: "Go to Settings", detail: "Open your profile → Settings → Account." },
          { title: "Notification sound", detail: "Enable or disable message notification sounds. The setting takes effect immediately." },
        ],
      },
    ],
  },

  {
    id: "marketplace",
    icon: 'ShoppingBag',
    title: "Marketplace",
    color: "text-yellow-500",
    bg: "bg-yellow-500/10",
    intro: "Buy and sell beats, samples, merchandise, services, and more.",
    features: [
      {
        name: "Browsing & filtering",
        summary: "Find items by category, view, and sort.",
        steps: [
          { title: "Go to Marketplace", detail: "Tap Marketplace in the sidebar to open the full listing." },
          { title: "View modes", detail: "Switch between Grid, List, and Compact (tracklist-style) views using the icons in the top bar." },
          { title: "Sort", detail: "Sort by Default, Price (low→high), or A–Z using the sort dropdown." },
          { title: "QR share", detail: "Every listing has a ▣ QR button to share the item link." },
        ],
      },
      {
        name: "Pay What You Want (PWYW)",
        summary: "Name your price on eligible items.",
        steps: [
          { title: "Look for the 'Name Your Price' label", detail: "PWYW listings show a 'Name Your Price' badge on their card." },
          { title: "Enter your offer", detail: "Tap the item, then tap 'Name Your Price'. A dialog asks for your amount — enter anything at or above the minimum." },
          { title: "Checkout", detail: "Stripe processes your payment. You receive the download or content immediately after." },
        ],
      },
      {
        name: "Selling an item",
        summary: "List your beats, samples, or services for sale.",
        steps: [
          { title: "Go to your Profile", detail: "Open your profile page and tap the Marketplace tab." },
          { title: "Add a listing", detail: "Tap the + button to open the Add Item dialog." },
          { title: "Fill in details", detail: "Title, description, price (or PWYW minimum), images, download type (free / PWYW / paid), and category." },
          { title: "Publish", detail: "Tap Save. Your listing appears live on the marketplace immediately." },
        ],
        userTypes: ["creator", "venue"],
      },
      {
        name: "Reporting a listing",
        summary: "Flag inappropriate or misleading items.",
        steps: [
          { title: "Tap Report", detail: "On any marketplace item page, scroll to the bottom and tap 'Report'." },
          { title: "Choose a reason", detail: "Select from: Inappropriate content, Misleading information, Copyright issue, or Other." },
          { title: "Submit", detail: "Your report goes to the admin team (Super Admin → Reports tab). You'll see a confirmation." },
        ],
      },
    ],
  },

  {
    id: "creators",
    icon: 'Mic',
    title: "Creator Tools",
    color: "text-red-500",
    bg: "bg-red-500/10",
    intro: "Upload music, manage releases, bulk edit, use AI credits extraction, sell downloads, and track earnings.",
    features: [
      {
        name: "Media Vault",
        summary: "Your personal library — every track, release, and file you've uploaded.",
        steps: [
          { title: "Open Media Vault", detail: "Tap 'Media' in the sidebar. Your full catalogue appears with a sticky header showing totals." },
          { title: "Albums & Releases", detail: "The top section shows a collapsible grid of all your releases (albums, EPs, singles). Click any cover to open the release." },
          { title: "Track list", detail: "Below releases is every individual track. Columns show title, artist, type, genre, album, and status." },
          { title: "Trash & restore", detail: "Deleted tracks go to a 30-day soft-delete. Tap 'Trash' to view and restore them before they're permanently removed." },
        ],
        userTypes: ["creator", "admin"],
      },
      {
        name: "Uploading a single track",
        summary: "Upload one track with full metadata.",
        steps: [
          { title: "Tap 'Single Upload'", detail: "In the Media Vault header, tap 'Single Upload'. A dialog opens." },
          { title: "Drop or browse your file", detail: "Drag an MP3, WAV, AIFF, or FLAC file. ID3 tags (title, artist, genre, year) auto-fill from the file." },
          { title: "Fill in metadata", detail: "Add cover art, ISRC code, contributors, rights holder, lyrics, and privacy setting." },
          { title: "Publish", detail: "Tap Publish. The track appears live in your catalogue and on the platform." },
        ],
        userTypes: ["creator", "admin"],
      },
      {
        name: "Album / EP Upload",
        summary: "Upload multiple tracks and auto-create a release doc.",
        steps: [
          { title: "Tap 'Album/EP Upload'", detail: "Opens the bulk upload flow. Add multiple audio files at once via drag-and-drop." },
          { title: "Shared artwork", detail: "Upload artwork once — it applies to all tracks. Supports up to 8 images; the first becomes the cover." },
          { title: "AI Credits parser", detail: "Paste raw liner notes or Discogs text into the 'Parse Credits from Text' field. AI extracts title, year, artist, label, contributors, and track listing — all fields pre-fill automatically." },
          { title: "Choose release type", detail: "Select Album, EP, Single, Mixtape, Compilation, or Live Album. After all tracks publish, a Release doc is created automatically linking them all." },
          { title: "Multi-genre tags", detail: "Each track supports multiple genres — type and add them as pill tags." },
        ],
        userTypes: ["creator", "admin"],
        badge: "AI-powered",
      },
      {
        name: "AI Credits extractor",
        summary: "Scan back-cover artwork to extract credits automatically.",
        steps: [
          { title: "In the upload form", detail: "In either the single or bulk upload form, scroll to the artwork section." },
          { title: "Tap 'Extract Credits with AI'", detail: "The button sends the uploaded artwork image to Gemini Vision AI." },
          { title: "Review results", detail: "Extracted contributors (name, role, instrument) appear in the credits list. Duplicates are skipped. Add or remove as needed." },
        ],
        userTypes: ["creator", "admin"],
        badge: "AI-powered",
      },
      {
        name: "Bulk edit",
        summary: "Apply changes to multiple tracks at once.",
        steps: [
          { title: "Select tracks", detail: "In Media Vault, tick the checkbox on one or more track rows. A 'Select All' checkbox appears in the header." },
          { title: "Open bulk edit panel", detail: "An action bar slides up showing how many tracks are selected. Tap 'Edit Selected' to open the inline bulk edit panel." },
          { title: "Apply changes", detail: "Set Artist, Genre, Type, Status, Privacy, Publisher, Explicit flag, or Allow Downloads. Only fields you fill in will overwrite existing values — blank fields are left untouched." },
          { title: "Bulk erase (danger)", detail: "To permanently delete all selected tracks, tap 'Erase' and type the word ERASE to confirm. This sends them to the 30-day trash." },
        ],
        userTypes: ["creator", "admin"],
      },
      {
        name: "Earnings & Analytics",
        summary: "Track plays, downloads, and revenue.",
        steps: [
          { title: "Open Earnings", detail: "Tap 'Earnings' in the sidebar. Requires a Creator account." },
          { title: "Play analytics", detail: "See total play counts, downloads, and revenue over time. Charts break down by track." },
          { title: "Payouts", detail: "Connect your Stripe account in Settings → Billing to receive direct payouts from sales." },
        ],
        userTypes: ["creator"],
      },
      {
        name: "Artwork Studio",
        summary: "Generate and edit release artwork with AI.",
        steps: [
          { title: "Open Artwork Studio", detail: "Tap 'Artwork Studio' in the sidebar under Creator Tools." },
          { title: "Describe your vision", detail: "Type a prompt describing the artwork style, mood, colours, and subject." },
          { title: "Generate", detail: "AI generates multiple artwork options. Download or apply directly to a release." },
          { title: "Enhance the prompt", detail: "Tap 'Enhance Prompt' to let AI improve your description for better results." },
        ],
        userTypes: ["creator", "admin"],
        badge: "AI-powered",
      },
      {
        name: "Paid downloads & Name Your Price",
        summary: "Monetise your tracks with flexible pricing.",
        steps: [
          { title: "Enable downloads on a track", detail: "In the track edit form (Media Vault → pencil icon), find the 'Downloads' section." },
          { title: "Set the pricing type", detail: "Choose Free, Pay What You Want (with a minimum), or Fixed Price (in your currency)." },
          { title: "Listeners purchase", detail: "On the track's page, listeners see a Download button. PWYW opens a price dialog; paid tracks go through Stripe checkout." },
        ],
        userTypes: ["creator"],
      },
      {
        name: "Bookkeeping",
        summary: "Track your income, expenses, invoices, and fiscal summary.",
        steps: [
          { title: "Open Bookkeeping", detail: "Tap 'Bookkeeping' in the sidebar." },
          { title: "Transactions tab", detail: "Log income and expenses manually. Each entry has a category, amount, date, and notes." },
          { title: "Invoices tab", detail: "Create and send professional invoices to clients. Track paid vs unpaid." },
          { title: "Fiscal summary", detail: "The summary tab shows your yearly P&L, tax estimate, and category breakdowns." },
        ],
        userTypes: ["creator"],
      },
    ],
  },

  {
    id: "social",
    icon: 'Heart',
    title: "Profile & Social",
    color: "text-rose-500",
    bg: "bg-rose-500/10",
    intro: "Customise your public profile wall, post updates, pin content, and connect your social platforms.",
    features: [
      {
        name: "Profile Wall",
        summary: "Your public profile — posts, pinned content, and a custom theme.",
        steps: [
          { title: "Open your profile", detail: "Tap your avatar in the sidebar or top bar. Your profile page has tabs: Wall, Music, Videos, Marketplace, Playlists, and more." },
          { title: "Wall posts", detail: "The Wall tab is your public feed. Use the composer at the top to write posts. Choose privacy: Public (everyone), Followers only, or Private (only you)." },
          { title: "Pinned content blocks", detail: "Scroll down in Wall Studio to the Pinned Blocks section — add artist profiles, tracks, releases, videos, marketplace items, events, images, or links. Drag to reorder them." },
        ],
      },
      {
        name: "Wall Studio",
        summary: "Customise your wall's background, colours, font, and pinned content.",
        steps: [
          { title: "Tap 'Edit Wall'", detail: "The Edit Wall button appears in your wall header when logged in as yourself." },
          { title: "Choose a theme", detail: "Pick from 8 preset themes: Dark Street, Neon Nights, Concrete, Vintage, Ocean, Forest, Clean, or Custom." },
          { title: "Customise", detail: "For Custom: set background type (solid colour / image / gradient), overlay darkness, text colour, accent colour, and font (System / Serif / Mono / Display)." },
          { title: "Preview and save", detail: "A live mini-preview updates as you change settings. Tap Save to apply — visitors see the changes immediately." },
        ],
      },
      {
        name: "View as Visitor",
        summary: "Preview exactly how your profile looks to someone else.",
        steps: [
          { title: "Tap the eye icon", detail: "The 👁 button in your profile header switches on Visitor Mode." },
          { title: "See your public view", detail: "A banner confirms you're in visitor view. Only the tabs visible to public visitors are shown — Wall, Music, Videos, Marketplace, Playlists." },
          { title: "Switch back", detail: "Tap 'Exit Visitor View' in the banner to return to your normal profile view." },
        ],
      },
      {
        name: "Following & Followers",
        summary: "Follow artists and users; build your own audience.",
        steps: [
          { title: "Follow someone", detail: "Tap the Follow button on any artist page or user profile. They appear in your Following list." },
          { title: "Following feed", detail: "Your Following list feeds the 'Following' content mode on the home page — only their content shows." },
          { title: "Followers", detail: "When someone follows you, they appear in your Followers list. Creators see their follower count on their profile." },
        ],
      },
      {
        name: "Social links",
        summary: "Connect your Spotify, SoundCloud, Instagram, and other platforms.",
        steps: [
          { title: "Go to Profile → Social", detail: "Your profile's Social tab shows connected platforms as cards." },
          { title: "Add a platform", detail: "Tap Add Platform and paste your profile URL (Spotify, SoundCloud, Instagram, YouTube, Twitter, Bandcamp, etc.)." },
          { title: "How they appear", detail: "Each platform shows as a card with the site's favicon, your username, and a Visit button. Spotify and SoundCloud also show an embed player." },
        ],
      },
    ],
  },

  {
    id: "events",
    icon: 'Ticket',
    title: "Events",
    color: "text-indigo-500",
    bg: "bg-indigo-500/10",
    intro: "Discover upcoming events, get tickets, and join real-time Listening Parties.",
    features: [
      {
        name: "Browsing events",
        summary: "Find upcoming concerts, shows, and listening events.",
        steps: [
          { title: "Events section on home feed", detail: "Scroll to the Events section on the home page. Cards show event name, date, venue, and image." },
          { title: "Events page", detail: "Tap 'Events' in the sidebar for the full listing. Filter by date or genre." },
          { title: "Event detail", detail: "Each event page shows full details, venue info, ticket links, description, and a QR code for sharing." },
        ],
      },
      {
        name: "Listening Party",
        summary: "Listen to the same track in perfect sync with friends across devices.",
        steps: [
          { title: "Create a party", detail: "Go to /party (tap 'Listening Party' in the sidebar). Tap 'Create Party' — you get a 6-digit party code." },
          { title: "Invite guests", detail: "Share the code with friends. They go to /party and enter the code to join." },
          { title: "Host controls", detail: "As host, you control play, pause, seek, and track selection. All participants hear the same moment simultaneously." },
          { title: "Sync accuracy", detail: "The app uses NTP-style clock synchronisation and compensates for Bluetooth/speaker latency. Members see their latency in ms in the party room." },
          { title: "Acoustic sync", detail: "Tap the 🎤 button to use microphone-based acoustic sync — a brief 880Hz tone burst calibrates all devices in the room." },
        ],
        badge: "Live",
      },
    ],
  },

  {
    id: "radio",
    icon: 'Radio',
    title: "Radio",
    color: "text-cyan-500",
    bg: "bg-cyan-500/10",
    intro: "Stream curated radio stations, browse by genre, or tune into the AI-powered BB Radio DJ.",
    features: [
      {
        name: "Radio stations",
        summary: "Browse and stream curated stations by genre.",
        steps: [
          { title: "Open Radio", detail: "Tap 'Radio' in the sidebar. Stations are organised by genre with artwork and current track info." },
          { title: "Tap a station", detail: "Tapping any station card starts streaming in the media player. The player shows the station name and current track." },
          { title: "Meet the DJs", detail: "Scroll down on the Radio page to find featured DJs — tap any to see their profile and listen to their shows." },
        ],
      },
      {
        name: "DJ Studio",
        summary: "Create and broadcast your own DJ mix.",
        steps: [
          { title: "Open DJ Studio", detail: "Tap 'DJ Studio' under Radio in the sidebar." },
          { title: "Load tracks", detail: "Search and load tracks from the catalogue into Deck A and Deck B." },
          { title: "Mix and crossfade", detail: "Use the crossfader and EQ controls to blend between decks. Record the session or broadcast live." },
        ],
        userTypes: ["creator", "admin"],
      },
      {
        name: "BB AI Radio DJ",
        summary: "AI hosts a personalised radio show just for you.",
        steps: [
          { title: "Tap 'AI Radio'", detail: "Found on the Radio page. The AI DJ generates a personalised playlist and hosts the session with voice commentary." },
          { title: "Interact", detail: "Tell the AI DJ what mood, genre, or era you want. It adjusts the programme in real time." },
        ],
        badge: "AI-powered",
      },
    ],
  },

  {
    id: "publishing",
    icon: 'FileText',
    title: "Publishing & Sync",
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    intro: "Manage publishing royalties, claim unregistered works, and browse sync licensing opportunities.",
    features: [
      {
        name: "⚠️ Two royalties, two societies — register both every time you publish",
        summary: "Breakin' Bread is not a collecting society. There are TWO separate royalty streams and TWO different societies. As a songwriter register the SONG (STIM in Sweden). As a performer register the RECORDING (SAMI in Sweden). You often need both.",
        steps: [
          { title: "Understand the split", detail: "① The SONG (composition — the lyrics & melody you wrote) earns SONGWRITER royalties, collected by a PRO like STIM. ② The RECORDING (your actual performance on the master) earns PERFORMER / 'neighbouring rights' royalties, collected by a society like SAMI. They are different money paid by different societies — if you only join one, you leave the other half on the table." },
          { title: "Publish on BB first", detail: "Upload and publish your track/release on Breakin' Bread so your metadata (title, artist, ISRC, ISWC, credits, splits) is finalised — you'll copy these into both registrations." },
          { title: "① Register the SONG with your PRO (songwriter side)", detail: "If you wrote the composition, register it with a Performing Rights Organisation: STIM (Sweden), or ASCAP / BMI (US), PRS (UK), GEMA (Germany), SACEM (France), Koda (Denmark), TONO (Norway), Teosto (Finland), SOCAN (Canada), etc. Enter the songwriters and their ownership percentages exactly as your BB credits, plus the ISWC (work code). Not a member yet? Join one first — you can only collect through the society you're affiliated with." },
          { title: "② Register the RECORDING as a performer (SAMI side)", detail: "If you sang or played on the recording, register with your neighbouring-rights society — SAMI in Sweden. This is a SEPARATE membership from STIM. See the next article ('Register as a performer') for the full country-by-country list of who to join." },
          { title: "Use the SAME title, splits and credits as on BB", detail: "Matching metadata — title, ISRC (recording code), ISWC (work code), contributor names and percentages — is what lets each society route royalties back to you. Mismatched data is the #1 reason money goes unpaid." },
          { title: "Why this matters", detail: "BB pays you directly for sales and downloads here. But radio, TV, streaming, venues, gyms and cafés generate performance royalties (for the song, via STIM) AND performer royalties (for the recording, via SAMI) that ONLY those societies can collect. Unregistered = uncollected." },
          { title: "Do it every release", detail: "Every new song (and every new version/remix) needs its own registration on both sides. Make it a habit right after you hit Publish." },
          { title: "Coming to BB", detail: "We're working on registering works for you automatically. Until that's live, please register with STIM/your PRO and SAMI/your performer society yourself each time." },
        ],
        userTypes: ["creator"],
      },
      {
        name: "⚠️ Register as a PERFORMER — SAMI (Sweden) & your country's equivalent",
        summary: "If you sang or played on a recording you're owed 'neighbouring rights' whenever it's played in public — radio, TV, streaming, shops, gyms, venues. In Sweden that's SAMI. Every country has its own performer society. This is separate from STIM and separate from BB.",
        steps: [
          { title: "What SAMI collects", detail: "SAMI (Svenska Artisters och Musikers Intresseorganisation) pays featured artists AND session musicians when a recording they performed on is broadcast or played publicly in Sweden. It is the performer's half of 'neighbouring rights' — completely separate from STIM (which pays the songwriter) and from the money BB pays you for sales." },
          { title: "Join SAMI (Sweden)", detail: "Sign up free at sami.se, then register each recording you performed on with its ISRC, the release title, and your role (lead vocals, guitar, producer, etc.). SAMI also collects your international performer royalties via reciprocal deals with 90+ foreign societies — so joining one society at home captures plays abroad too." },
          { title: "Not in Sweden? Find your country's performer society", detail: "Join the neighbouring-rights society where you're tax-resident: 🇬🇧 UK — PPL · 🇺🇸 US — SoundExchange · 🇩🇪 Germany — GVL · 🇫🇷 France — ADAMI & SPEDIDAM · 🇳🇱 Netherlands — SENA · 🇩🇰 Denmark — Gramex · 🇳🇴 Norway — Gramo · 🇫🇮 Finland — Gramex · 🇪🇸 Spain — AIE · 🇮🇹 Italy — Nuovo IMAIE · 🇵🇱 Poland — STOART / SAWP · 🇨🇦 Canada — Re:Sound · 🇦🇺 Australia — PPCA · 🇮🇪 Ireland — RAAP · 🇳🇿 New Zealand — Recorded Music NZ." },
          { title: "Can't find yours? Use the directories", detail: "Performer societies are members of SCAPR (scapr.org) — its member directory lists the right society for almost every country. FIM (fim-musicians.org) also publishes a collecting-societies list. Pick the one for the country where you live and pay tax." },
          { title: "Featured artist vs session player", detail: "You can register whether you're the named artist OR a hired musician/backing vocalist on someone else's record — performer royalties are split between the two. Register every recording you actually played on, not just your own releases." },
          { title: "Don't forget the master owner", detail: "Neighbouring rights have two halves: the PERFORMER (you, via SAMI) and the MASTER RIGHTS HOLDER (whoever owns the recording — you, if you self-released, otherwise your label). If you own your masters, make sure the producer/label share is claimed too (in Sweden that's handled via IFPI; PPL/SoundExchange/GVL cover both halves in their countries)." },
        ],
        userTypes: ["creator"],
      },
      {
        name: "Publishing dashboard",
        summary: "Track royalty earnings and manage your publishing catalogue.",
        steps: [
          { title: "Open Publishing", detail: "Tap 'Publishing' in the sidebar." },
          { title: "Royalties tab", detail: "Shows earnings by track — streams, downloads, and sync placements." },
          { title: "Unclaimed tab", detail: "Lists works that may be generating royalties but aren't yet registered with a PRO. Claim them to start collecting." },
          { title: "Verification tab", detail: "Upload certificates and documentation to verify your publishing entity with the platform." },
        ],
        userTypes: ["creator"],
      },
      {
        name: "Sync licensing",
        summary: "Browse briefs from TV, film, and ad clients and pitch your music.",
        steps: [
          { title: "Open Synchronization", detail: "Tap 'Sync' in the sidebar." },
          { title: "Browse briefs", detail: "Active sync briefs show the client, medium (TV / film / ad / game), mood, and budget range." },
          { title: "Submit a track", detail: "Tap a brief, then 'Submit Track'. Pick a track from your catalogue that fits the brief's requirements." },
          { title: "Track placements", detail: "The Placements tab shows your submitted tracks and their status." },
        ],
        userTypes: ["creator"],
      },
    ],
  },

  {
    id: "legal-ai",
    icon: 'Scale',
    title: "AI Legal Department",
    color: "text-violet-500",
    bg: "bg-violet-500/10",
    intro: "A proactive AI system that monitors rights, analyses contracts, and flags issues across all BB content.",
    features: [
      {
        name: "Active Issues",
        summary: "Real-time alerts for rights issues detected across the platform.",
        steps: [
          { title: "Open Legal AI", detail: "Tap 'Legal AI' in the sidebar (Scales icon)." },
          { title: "Active Issues panel", detail: "Shows detected rights conflicts, unregistered works, expired licences, and missing splits — colour-coded by priority." },
          { title: "Drill into an issue", detail: "Tap any issue to see full details, affected parties, and recommended actions." },
        ],
        userTypes: ["admin", "creator"],
      },
      {
        name: "Rights Checker",
        summary: "Instantly check ownership and rights status for any track or work.",
        steps: [
          { title: "Enter a track or ISRC", detail: "In the Rights Checker panel, type a track name or ISRC code." },
          { title: "Run the check", detail: "The AI cross-references registered rights, PRO data, and platform records." },
          { title: "Review the report", detail: "Results show ownership splits, registration status, and any conflicts." },
        ],
      },
      {
        name: "Contract Scanner",
        summary: "Photograph or upload a contract — get it explained, see every date, set reminders and write your counter-proposal.",
        steps: [
          { title: "Open Contract Scanner", detail: "Tap the Contract Scanner tab in the Legal AI page." },
          { title: "Scan it", detail: "Take a photo of each page, upload a PDF, or paste the text, then tap Scan contract (10 tokens up to 20 pages). Only you can see your contracts." },
          { title: "Read it in plain words", detail: "Overview explains what you agree to; Red flags lists risky terms (360 deals, in-perpetuity rights, no audit right, auto-renewal) with what to ask for instead." },
          { title: "Never miss a date", detail: "Dates & reminders lists every deadline, notice period and payment date. Tap Email me for a reminder from BB, or Add to calendar for alarms on your phone." },
          { title: "Counter-propose", detail: "Tick the changes you want, add notes, pick tone and language, and get an email ready to send from your own mail app (2 tokens). Ask answers questions about that contract (2 tokens)." },
        ],
        badge: "AI-powered",
      },
      {
        name: "Ask Legal AI",
        summary: "Chat directly with the AI legal advisor.",
        steps: [
          { title: "Open the Ask panel", detail: "Tap 'Ask Legal AI' in the panel tabs." },
          { title: "Type your question", detail: "Ask anything about music law, rights, contracts, royalties, or publishing. The AI gives jurisdiction-aware answers (Sweden / EU / US)." },
          { title: "Case Files", detail: "Important conversations and documents can be saved as Case Files — accessible any time under the Case Files tab." },
        ],
      },
    ],
  },

  {
    id: "search",
    icon: 'Search',
    title: "Search",
    color: "text-violet-500",
    bg: "bg-violet-500/10",
    intro: "Find anything on the platform instantly — artists, music, events, news, and marketplace items.",
    features: [
      {
        name: "Global search",
        summary: "Search across the entire platform from the header.",
        steps: [
          { title: "Tap the magnifier icon", detail: "The 🔍 icon in the top-right header opens a full search overlay." },
          { title: "Type to search", detail: "Results appear in real time as you type — grouped by Artists, Music, Releases, Events, News, and Marketplace." },
          { title: "Navigate results", detail: "Tap any result to go directly to that page." },
        ],
      },
      {
        name: "Home page search",
        summary: "Filter everything visible on the home feed without leaving the page.",
        steps: [
          { title: "Use the search bar on home", detail: "The search input at the top of the home feed filters all visible sections simultaneously — no page reload." },
          { title: "Live filter", detail: "As you type, every section (Artists, Music, Events, etc.) updates to show only matching items." },
          { title: "Clear to reset", detail: "Tap the × in the search bar to clear and restore all sections." },
        ],
      },
    ],
  },
];
